<template>
  <div class="executive-dashboard">
    <ExecutiveFiltersBar
      :filters.sync="filters"
      :state-options="executiveFilters.states"
      :culture-options="executiveFilters.cultures"
      :technician-options="executiveFilters.technicians"
      :period-options="executiveFilters.periods"
      @export-report="exportExecutiveReport"
      @view-map="scrollToRegional"
    />

    <ExecutiveKpiCards :items="kpis" />

    <ExecutiveRegionalSection :table-rows="regionalSummaryRows" />

    <div class="row">
      <div class="col-12 col-xl-6">
        <ExecutiveCultivarSection :items="cultivarPerformance" />
      </div>
      <div class="col-12 col-xl-6">
        <ExecutiveStateComparisonSection
          :categories="receitasPorEstado6Meses.categories"
          :series="receitasPorEstado6Meses.series"
        />
      </div>
    </div>

    <ExecutiveInsightsSection
      :insights="safraInsights"
      :weather-insight="weatherInsight"
      :weather-loading="isWeatherLoading"
    />

    <ExecutiveApplicationGuide :weather-insight="weatherInsight" :weather-loading="isWeatherLoading" />

    <ExecutiveTechnicalSection :indicators="technicalIndicators" />

    <ExecutiveChatAssistant :token="authToken" :context="chatContext" />
  </div>
</template>

<script>
import ExecutiveFiltersBar from './ExecutiveFiltersBar.vue'
import ExecutiveKpiCards from './ExecutiveKpiCards.vue'
import ExecutiveRegionalSection from './ExecutiveRegionalSection.vue'
import ExecutiveCultivarSection from './ExecutiveCultivarSection.vue'
import ExecutiveStateComparisonSection from './ExecutiveStateComparisonSection.vue'
import ExecutiveInsightsSection from './ExecutiveInsightsSection.vue'
import ExecutiveTechnicalSection from './ExecutiveTechnicalSection.vue'
import ExecutiveChatAssistant from './ExecutiveChatAssistant.vue'
import ExecutiveApplicationGuide from './ExecutiveApplicationGuide.vue'
import weatherCoordsService from '@/src/services/weather-coords'

import {
  executiveFilters,
  regionalRecords,
  kpiBaseline,
  cultivarPerformance,
  receitasPorEstado6Meses,
  safraInsights,
  technicalIndicators,
} from '../../mocks/executive-profile-12'

export default {
  name: 'ExecutiveDashboardProfile12',
  components: {
    ExecutiveFiltersBar,
    ExecutiveKpiCards,
    ExecutiveRegionalSection,
    ExecutiveCultivarSection,
    ExecutiveStateComparisonSection,
    ExecutiveInsightsSection,
    ExecutiveApplicationGuide,
    ExecutiveTechnicalSection,
    ExecutiveChatAssistant,
  },
  data() {
    return {
      executiveFilters,
      kpiBaseline,
      cultivarPerformance,
      receitasPorEstado6Meses,
      safraInsights,
      technicalIndicators,
      records: regionalRecords,
      isWeatherLoading: false,
      weatherInsight: null,
      userCoords: null,
      filters: {
        state: 'Todos',
        culture: 'Todas',
        technician: 'Todos',
        period: 'Ultimos 30 dias',
      },
    }
  },
  mounted() {
    this.resolveBrowserCoords().finally(() => {
      this.loadWeatherInsight()
    })
  },
  watch: {
    'filters.state'() {
      this.loadWeatherInsight()
    },
  },
  computed: {
    filteredRecords() {
      return this.records.filter((item) => {
        const stateOk = this.filters.state === 'Todos' || item.state === this.filters.state
        const cultureOk = this.filters.culture === 'Todas' || item.culture === this.filters.culture
        const techOk = this.filters.technician === 'Todos' || item.technician === this.filters.technician
        return stateOk && cultureOk && techOk
      })
    },
    regionalSummaryRows() {
      const grouped = this.filteredRecords.reduce((acc, row) => {
        if (!acc[row.state]) {
          acc[row.state] = {
            state: row.state,
            receitas: 0,
            produtores: 0,
            cultivares: 0,
            tecnicosAtivos: 0,
          }
        }
        acc[row.state].receitas += row.receitas
        acc[row.state].produtores += row.produtores
        acc[row.state].cultivares += row.cultivares
        acc[row.state].tecnicosAtivos += row.tecnicosAtivos
        return acc
      }, {})

      return ['PR', 'SC', 'RS'].map((state) => {
        return grouped[state] || {
          state,
          receitas: 0,
          produtores: 0,
          cultivares: 0,
          tecnicosAtivos: 0,
        }
      })
    },
    totals() {
      return this.filteredRecords.reduce(
        (acc, item) => {
          acc.receitas += item.receitas
          acc.produtores += item.produtores
          acc.tecnicos += item.tecnicosAtivos
          acc.hectares += item.hectares
          return acc
        },
        { receitas: 0, produtores: 0, tecnicos: 0, hectares: 0 }
      )
    },
    kpis() {
      const receitasGrowth = this.calculateGrowth(this.totals.receitas, this.kpiBaseline.receitasMesAnterior)
      const produtoresGrowth = this.calculateGrowth(
        this.totals.produtores,
        this.kpiBaseline.produtoresAtivosAnterior
      )
      const tecnicosGrowth = this.calculateGrowth(this.totals.tecnicos, this.kpiBaseline.tecnicosAtivosAnterior)
      const hectaresGrowth = this.calculateGrowth(
        this.totals.hectares,
        this.kpiBaseline.hectaresAssistidosAnterior
      )
      const regionalGrowth = this.calculateGrowth(receitasGrowth, this.kpiBaseline.crescimentoRegionalAnterior)

      return [
        {
          key: 'receitas',
          title: 'Receitas Emitidas no Mes',
          value: this.formatNumber(this.totals.receitas),
          subtext: 'Comparativo com mes anterior',
          growth: receitasGrowth,
          icon: 'file-text',
          iconClass: 'icon-income',
        },
        {
          key: 'produtores',
          title: 'Produtores Ativos',
          value: this.formatNumber(this.totals.produtores),
          subtext: 'Ultimos 30 dias',
          growth: produtoresGrowth,
          icon: 'users',
          iconClass: 'icon-user',
        },
        {
          key: 'tecnicos',
          title: 'Tecnicos Ativos',
          value: this.formatNumber(this.totals.tecnicos),
          subtext: 'Equipe de campo em operacao',
          growth: tecnicosGrowth,
          icon: 'briefcase',
          iconClass: 'icon-team',
        },
        {
          key: 'hectares',
          title: 'Hectares Assistidos',
          value: this.formatNumber(this.totals.hectares),
          subtext: 'Estimativa consolidada',
          growth: hectaresGrowth,
          icon: 'map',
          iconClass: 'icon-field',
        },
        {
          key: 'regional',
          title: 'Crescimento Regional',
          value: `${receitasGrowth.toFixed(1)}%`,
          subtext: 'Expansao operacional no Sul',
          growth: regionalGrowth,
          icon: 'trending-up',
          iconClass: 'icon-growth',
        },
      ]
    },
    authToken() {
      const auth =
        this.$store && this.$store.state && this.$store.state.auth
          ? this.$store.state.auth.currentUser
          : null
      return auth && auth.token ? auth.token : ''
    },
    chatContext() {
      return {
        filters: this.filters,
        totals: this.totals,
        kpis: this.kpis.map((item) => ({
          key: item.key,
          title: item.title,
          value: item.value,
          growth: item.growth,
        })),
      }
    },
  },
  methods: {
    resolveBrowserCoords() {
      return new Promise((resolve) => {
        if (!navigator || !navigator.geolocation) {
          resolve(null)
          return
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.userCoords = {
              lat: Number(position.coords.latitude),
              lng: Number(position.coords.longitude),
              label: '',
            }
            resolve(this.userCoords)
          },
          () => {
            resolve(null)
          },
          {
            enableHighAccuracy: true,
            timeout: 8000,
            maximumAge: 10 * 60 * 1000,
          }
        )
      })
    },
    getCoordsByState() {
      if (this.filters.state === 'Todos' && this.userCoords) {
        return this.userCoords
      }
      const state = this.filters.state === 'Todos' ? 'PR' : this.filters.state
      const map = {
        PR: { lat: -25.4284, lng: -49.2733, label: 'PR' },
        SC: { lat: -27.5954, lng: -48.548, label: 'SC' },
        RS: { lat: -30.0346, lng: -51.2177, label: 'RS' },
      }
      return map[state] || map.PR
    },
    getNumberFromPaths(obj, paths, fallback = null) {
      for (let i = 0; i < paths.length; i += 1) {
        const keys = paths[i].split('.')
        let current = obj
        for (let j = 0; j < keys.length; j += 1) {
          if (current && Object.prototype.hasOwnProperty.call(current, keys[j])) {
            current = current[keys[j]]
          } else {
            current = null
            break
          }
        }
        const value = Number(current)
        if (!Number.isNaN(value) && value !== 0) {
          return value
        }
      }
      return fallback
    },
    buildDefensiveRecommendation(weatherData, stateLabel) {
      const day = weatherData && weatherData.forecast && weatherData.forecast.days
        ? weatherData.forecast.days[0]
        : null

      const wind = day ? Number(day.windKmh || 0) : 0
      const gust = day ? Number(day.gustKmh || 0) : 0
      const rainProb = day ? Number(day.rainProbDay || 0) : 0
      const humidity = day ? Number(day.humidityDay || 0) : 0
      const maxTemp = day ? Number(day.max || 0) : 0
      const minTemp = day ? Number(day.min || 0) : 0
      const uv = day ? Number(day.uv || 0) : 0

      let level = 'positive'
      let recommendation = 'Janela favoravel para aplicacao de defensivos, mantendo calibracao e monitoramento.'

      if (rainProb >= 70) {
        level = 'warning'
        recommendation =
          'Alta probabilidade de chuva no periodo. Recomenda-se adiar aplicacao de defensivos para evitar lavagem.'
      } else if (wind >= 15 || gust >= 25) {
        level = 'warning'
        recommendation =
          'Vento e/ou rajadas elevadas aumentam risco de deriva. Ajustar pontas, pressao e faixa de seguranca.'
      } else if (maxTemp >= 30 && humidity > 0 && humidity < 45) {
        level = 'warning'
        recommendation =
          'Condicao de calor com baixa umidade eleva evaporacao. Priorizar aplicacao no inicio da manha ou fim da tarde.'
      } else if (uv >= 9 || maxTemp >= 28) {
        level = 'info'
        recommendation =
          'Condicao moderada para aplicacao. Avaliar janela horaria e reforcar monitoramento de perdas por evaporacao.'
      }

      return {
        level,
        title: `Recomendação de Aplicação`,
        message: recommendation,
        source: 'API de Clima WebReceita (weather/coords)',
        metrics: {
          wind,
          gust,
          rainProb,
          humidity,
          temperature: maxTemp || null,
          minTemperature: minTemp || null,
          uv,
        },
      }
    },
    loadWeatherInsight() {
      if (!this.authToken) {
        return
      }

      const coords = this.getCoordsByState()
      this.isWeatherLoading = true

      weatherCoordsService
        .getByCoords(this.authToken, coords.lat, coords.lng)
        .then((response) => {
          const weatherData = response && response.data ? response.data : {}
          this.weatherInsight = this.buildDefensiveRecommendation(weatherData, coords.label)
        })
        .catch(() => {
          this.weatherInsight = {
            level: 'info',
            title: `Recomendação de Aplicação`,
            message:
              'Sem retorno da API de clima no momento. Utilize criterio tecnico local antes da aplicacao de defensivos.',
            source: 'API de Clima WebReceita (weather/coords)',
            metrics: null,
          }
        })
        .finally(() => {
          this.isWeatherLoading = false
        })
    },
    formatNumber(value) {
      return new Intl.NumberFormat('pt-BR').format(value)
    },
    calculateGrowth(current, previous) {
      if (!previous) {
        return 0
      }
      return Number((((current - previous) / previous) * 100).toFixed(1))
    },
    exportExecutiveReport() {
      const payload = {
        generatedAt: new Date().toISOString(),
        filters: this.filters,
        kpis: this.kpis,
        regionalSummary: this.regionalSummaryRows,
      }

      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json;charset=utf-8',
      })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'webreceita-dashboard-executivo.json'
      anchor.click()
      window.URL.revokeObjectURL(url)
    },
    scrollToRegional() {
      const target = document.getElementById('regional-map')
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
  },
}
</script>

<style scoped>
.executive-dashboard {
  padding-bottom: 12px;
}
</style>
